import type { RouteObject } from "react-router";
import { useNavigate } from "react-router";

import { Button, ButtonStyle, PageHeading } from "@prequel/shared-components";

import DatasetDetails from "./DatasetDetails";
import DatasetForm from "./DatasetForm";
import DatasetPage, { DatasetDetailsPage, useDatasetContext } from "./DatasetPage";
import DatasetsTable from "./DatasetsTable";
import ExtractDetails from "./ExtractDetails";
import ExtractPage from "./ExtractPage";
import ExtractRecordsTable from "./ExtractRecordsTable";
import ExtractsTable from "./ExtractsTable";

import Breadcrumbs from "@/components/Breadcrumbs";

const DatasetsListLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-6xl px-7 py-4 pb-16">
        <PageHeading title="Datasets" breadcrumbs={<Breadcrumbs />}>
          <Button text="Add Dataset" onClick={() => navigate("/import/datasets/new")} type={ButtonStyle.SECONDARY} />
        </PageHeading>
        <DatasetsTable />
      </div>
    </div>
  );
};

const DatasetFormNewLayout = () => {
  const navigate = useNavigate();

  return (
    <div className="h-full flex-col">
      <div className="max-w-4xl px-7 py-4">
        <PageHeading title="Add Dataset" onGoBack={() => navigate("/import/datasets")} breadcrumbs={<Breadcrumbs />} />
        <DatasetForm />
      </div>
    </div>
  );
};

const DatasetFormEditLayout = () => {
  const navigate = useNavigate();
  const { dataset, refreshDataset } = useDatasetContext();

  return (
    <div className="h-full flex-col">
      <div className="max-w-4xl px-7 py-4">
        <PageHeading
          title="Edit Dataset"
          onGoBack={() => navigate(`/import/datasets/${dataset.id}`)}
          breadcrumbs={
            <Breadcrumbs dynamicLabel={dataset.method === "table" ? dataset.table.source_table_name : dataset.id} />
          }
        />
        <DatasetForm
          datasetId={dataset.id}
          dataset={dataset}
          cancelPath={`/import/datasets/${dataset.id}`}
          onSaved={refreshDataset}
        />
      </div>
    </div>
  );
};

export const datasetsRouteChildren: RouteObject = {
  path: "datasets",
  handle: {
    breadcrumb: () => ({ label: "Datasets" }),
  },
  children: [
    {
      index: true,
      element: <DatasetsListLayout />,
    },
    {
      path: "new",
      handle: {
        breadcrumb: () => ({ label: "Add Dataset" }),
      },
      element: <DatasetFormNewLayout />,
    },
    {
      path: ":datasetID",
      handle: {
        breadcrumb: () => ({
          label: "Dataset Details",
          dynamic: true,
        }),
      },
      element: <DatasetPage />,
      children: [
        {
          path: "edit",
          handle: {
            breadcrumb: () => ({ label: "Edit Dataset" }),
          },
          element: <DatasetFormEditLayout />,
        },
        {
          element: <DatasetDetailsPage />,
          children: [
            {
              index: true,
              element: <ExtractsTable />,
            },
            {
              path: "details",
              element: <DatasetDetails />,
            },
          ],
        },
        {
          path: "extracts/:extractID",
          handle: {
            breadcrumb: () => ({
              label: "Extract Details",
              dynamic: true,
            }),
          },
          element: <ExtractPage />,
          children: [
            {
              index: true,
              element: <ExtractDetails />,
            },
            {
              path: "records",
              element: <ExtractRecordsTable />,
            },
          ],
        },
      ],
    },
  ],
};
